import React, { useState, useEffect, useCallback } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/api';
import { 
  BoletoInvoicePDFButton, 
  BoletoButtonImproved,
  BoletoEligibilityInfo,
  BoletoAPITester,
  RecreateBoletoButton
} from './';
import BoletoButtonDebug from './BoletoButtonDebug';
import * as pako from 'pako';

interface Boleto {
  boleto_id: string;
  invoice_id: string;
  boleto_number: string;
  due_date: string;
  amount: number;
  status: string;
  created_at: string;
  updated_at: string;
  barcode?: string;
  digitable_line?: string;
  pdf_url?: string;
  boleto_html?: string;
  has_html?: boolean;
}

interface BoletoHtmlModalProps {
  isOpen: boolean;
  onClose: () => void;
  boletoHtml: string;
  boletoNumber: string;
}

const BoletoHtmlModal: React.FC<BoletoHtmlModalProps> = ({ isOpen, onClose, boletoHtml, boletoNumber }) => {
  const [decodedHtml, setDecodedHtml] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const decodeHtml = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDebugInfo(null);
    
    try {
      // First, check if it's already plain HTML
      if (boletoHtml.trim().toLowerCase().startsWith('<html') || 
          boletoHtml.trim().toLowerCase().startsWith('<!doctype')) {
        setDecodedHtml(boletoHtml);
        setLoading(false);
        return;
      }
      
      // Try the backend decoding service first
      try {
        const response = await apiService.axiosInstance.post("/api/v1/boleto/decode-html", {
          html_data: boletoHtml,
          test_decode: true
        });
        
        const result = response.data;
        setDebugInfo(result);
        
        if (result.success && result.decoded_content) {
          setDecodedHtml(result.decoded_content);
          setLoading(false);
          return;
        } else {
          console.warn('Backend decoding failed:', result.error);
        }
      } catch (backendErr) {
        console.warn('Backend decoding service unavailable:', backendErr);
      }
      
      // Fallback to client-side decoding
      console.log('Attempting client-side decoding...');
      
      let decodedContent = null;
      
      // Method 1: Try simple base64 decode + gzip decompression
      try {
        const binaryString = atob(boletoHtml);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Try pako (gzip/zlib) decompression
        try {
          // Try gzip first
          decodedContent = pako.inflate(bytes, { to: 'string' });
          console.log('Successfully decoded with pako gzip');
        } catch (gzipErr) {
          try {
            // Try raw inflate (zlib)
            decodedContent = pako.inflateRaw(bytes, { to: 'string' });
            console.log('Successfully decoded with pako zlib');
          } catch (zlibErr) {
            console.log('Pako decompression failed, trying as uncompressed');
          }
        }
        
        // If pako didn't work, try as uncompressed
        if (!decodedContent) {
          const decoder = new TextDecoder('utf-8');
          decodedContent = decoder.decode(bytes);
          console.log('Decoded as uncompressed UTF-8');
        }
        
      } catch (base64Err) {
        console.log('Base64 decoding failed:', base64Err);
      }
      
      // Method 2: If base64 failed, maybe it's already decoded but compressed
      if (!decodedContent) {
        try {
          const encoder = new TextEncoder();
          const bytes = encoder.encode(boletoHtml);
          decodedContent = pako.inflate(bytes, { to: 'string' });
          console.log('Successfully decoded direct compression');
        } catch (directErr) {
          console.log('Direct compression decode failed:', directErr);
        }
      }
      
      // Method 3: Last resort - use as is if it contains HTML-like content
      if (!decodedContent && (boletoHtml.includes('<') && boletoHtml.includes('>'))) {
        decodedContent = boletoHtml;
        console.log('Using content as-is (appears to be HTML)');
      }
      
      if (decodedContent && decodedContent.trim()) {
        setDecodedHtml(decodedContent);
      } else {
        setError('Failed to decode boleto HTML. All decoding methods failed. The content may be corrupted or in an unsupported format.');
      }
      
    } catch (err) {
      console.error('Error decoding boleto HTML:', err);
      setError(`Failed to decode boleto HTML: ${err}`);
    } finally {
      setLoading(false);
    }
  }, [boletoHtml]);

  useEffect(() => {
    if (isOpen && boletoHtml) {
      decodeHtml();
    }
  }, [isOpen, boletoHtml, decodeHtml]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Boleto HTML - {boletoNumber}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading && (
            <div className="loading-container">
              <div className="spinner" />
              <p>Decoding boleto HTML...</p>
            </div>
          )}
          {error && (
            <div className="alert alert-danger">
              {error}
              {debugInfo && (
                <details style={{ marginTop: '10px' }}>
                  <summary>Debug Information</summary>
                  <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
          {!loading && !error && decodedHtml && (
            <div className="html-preview">
              <div className="html-actions">
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    const blob = new Blob([decodedHtml], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `boleto-${boletoNumber}.html`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download HTML
                </button>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    const newWindow = window.open();
                    if (newWindow) {
                      newWindow.document.write(decodedHtml);
                      newWindow.document.close();
                    }
                  }}
                >
                  Open in New Window
                </button>
                <button 
                  className="btn btn-sm btn-info"
                  onClick={() => {
                    navigator.clipboard.writeText(decodedHtml);
                    alert('HTML content copied to clipboard!');
                  }}
                >
                  Copy HTML
                </button>
              </div>
              <iframe
                srcDoc={decodedHtml}
                style={{ width: '100%', height: '500px', border: '1px solid #ddd' }}
                title={`Boleto ${boletoNumber}`}
              />
            </div>
          )}
          {!loading && !error && !decodedHtml && (
            <div className="alert alert-warning">
              No HTML content available for this boleto.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface InvoiceDetailsBoletosTabProps {
  invoice: Invoice;
  onRefreshInvoice: () => void;
}

const InvoiceDetailsBoletosTab: React.FC<InvoiceDetailsBoletosTabProps> = ({ 
  invoice, 
  onRefreshInvoice 
}) => {
  const [boletos, setBoletos] = useState<Boleto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBoletoHtml, setSelectedBoletoHtml] = useState<string | null>(null);
  const [selectedBoletoNumber, setSelectedBoletoNumber] = useState<string>('');
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(() => {
    const saved = localStorage.getItem('boleto-debug-visible');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [triggeringStatusUpdate, setTriggeringStatusUpdate] = useState(false);

  
  const fetchBoletos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch boletos for this invoice using the new API method
      const boletosData = await apiService.getInvoiceBoletos(invoice.invoice_id);
      
      // Show all boletos, not just those with HTML content
      setBoletos(boletosData || []);
    } catch (err: any) {
      console.error('Error fetching boletos:', err);
      setError(err.message || 'Failed to load boletos');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id]);

  useEffect(() => {
    fetchBoletos();
  }, [fetchBoletos]);

  const showBoletoHtml = (boleto: Boleto) => {
    if (boleto.boleto_html) {
      setSelectedBoletoHtml(boleto.boleto_html);
      setSelectedBoletoNumber(boleto.boleto_number);
      setShowHtmlModal(true);
    } else {
      alert('No HTML content available for this boleto.');
    }
  };

  const closeHtmlModal = () => {
    setShowHtmlModal(false);
    setSelectedBoletoHtml(null);
    setSelectedBoletoNumber('');
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'badge-warning',
      processing: 'badge-warning',
      created: 'badge-info',
      paid: 'badge-success',
      completed: 'badge-success',
      expired: 'badge-danger',
      cancelled: 'badge-secondary',
      failed: 'badge-danger',
      refunded: 'badge-secondary',
      disputed: 'badge-warning'
    };
    return classes[status.toLowerCase()] || 'badge-secondary';
  };

  const getStatusDisplayText = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'Pending',
      processing: 'Processing',
      created: 'Created',
      paid: 'Paid',
      completed: 'Completed',
      expired: 'Expired',
      cancelled: 'Cancelled',
      failed: 'Failed',
      refunded: 'Refunded',
      disputed: 'Disputed'
    };
    return statusMap[status.toLowerCase()] || status;
  };

  const handleBoletoCreated = (data: any) => {
    console.log('Boleto created:', data);
    fetchBoletos(); // Refresh the boletos list
    onRefreshInvoice(); // Refresh the invoice details
  };

  const handleBoletoError = (error: any) => {
    console.error('Boleto error:', error);
    setError(error.message || 'Failed to create boleto');
  };

  const toggleDebugInfo = () => {
    const newValue = !showDebugInfo;
    setShowDebugInfo(newValue);
    localStorage.setItem('boleto-debug-visible', JSON.stringify(newValue));
  };

  const triggerPaymentStatusUpdate = async () => {
    if (!boletos.length) {
      alert('No boletos found for this invoice.');
      return;
    }

    setTriggeringStatusUpdate(true);
    setError(null);

    try {
      console.log('Triggering payment status update for invoice:', invoice.invoice_id);
      
      const response = await apiService.axiosInstance.post(
        `/api/v1/boleto/trigger-status-update/${invoice.invoice_id}`
      );

      const result = response.data;
      console.log('Payment status update result:', result);

      if (result.success) {
        if (result.status_updated) {
          alert(`Payment status updated successfully!\n\nOld Status: ${result.old_status || 'None'}\nNew Status: ${result.new_status}\n\n${result.message}`);
          
          // Refresh boletos and invoice data
          await fetchBoletos();
          onRefreshInvoice();
        } else {
          alert(`Payment status is already up to date.\n\nCurrent Status: ${result.current_status || 'None'}\n\n${result.message}`);
        }
      } else {
        throw new Error(result.message || 'Failed to update payment status');
      }
    } catch (err: any) {
      console.error('Error triggering payment status update:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to trigger payment status update';
      setError(`Payment Status Update Error: ${errorMessage}`);
      alert(`Failed to update payment status: ${errorMessage}`);
    } finally {
      setTriggeringStatusUpdate(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Loading boletos...</p>
      </div>
    );
  }

  return (
    <div className="boletos-tab">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Boletos</h3>
            <div>
              {invoice.invoice_status !== 'draft' && (
                <>
                  <button
                    className="btn btn-primary"
                    style={{ marginRight: '10px' }}
                    title="Send updated boleto with late fees"
                    onClick={async () => {
                      try {
                        const resp = await apiService.boleto.sendUpdatedBoleto(invoice.invoice_id);
                        if (resp.success) {
                          alert('Updated boleto created and email sent.');
                          await fetchBoletos();
                          onRefreshInvoice();
                        } else {
                          alert('Failed to send updated boleto email');
                        }
                      } catch (e: any) {
                        const msg = e.response?.data?.detail || e.message || 'Failed to send updated boleto email';
                        alert(msg);
                      }
                    }}
                  >
                    ➕ Send Updated Boleto
                  </button>
                  <BoletoButtonImproved
                    invoiceId={invoice.invoice_id}
                    className="btn btn-warning"
                    style={{ marginRight: '10px' }}
                    onBoletoCreated={handleBoletoCreated}
                    onError={handleBoletoError}
                    debug={true}
                  />
                  <RecreateBoletoButton
                    invoiceId={invoice.invoice_id}
                    className="btn btn-danger"
                    style={{ marginRight: '10px' }}
                    onBoletoCreated={handleBoletoCreated}
                    onError={handleBoletoError}
                  />
                  <BoletoInvoicePDFButton
                    invoiceId={invoice.invoice_id}
                    language="pt-br"
                    className="btn btn-success"
                    variant="open"
                    createBoleto={true}
                    onError={handleBoletoError}
                  >
                    🇧🇷 PDF + Boleto
                  </BoletoInvoicePDFButton>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {/* Boleto Eligibility Information */}
          <BoletoEligibilityInfo invoice={invoice} />

          {/* Debug Information Section */}
          <div className="debug-section" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showDebugInfo ? '15px' : '0' }}>
              <h4 style={{ margin: 0, color: '#495057' }}>🔧 Debug Information</h4>
              <button
                onClick={toggleDebugInfo}
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: '12px' }}
              >
                {showDebugInfo ? '🙈 Hide Debug Info' : '👁️ Show Debug Info'}
              </button>
            </div>
            {showDebugInfo && (
              <div className="debug-components">
                <BoletoButtonDebug invoiceId={invoice.invoice_id} />
                <BoletoAPITester invoiceId={invoice.invoice_id} />
              </div>
            )}
          </div>

          {boletos.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Boleto Number</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {boletos.map((boleto) => (
                    <tr key={boleto.boleto_id}>
                      <td>{boleto.boleto_number}</td>
                      <td>{boleto.due_date ? new Date(boleto.due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{formatCurrency(boleto.amount, invoice.currency)}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(boleto.status)}`}>
                          {getStatusDisplayText(boleto.status)}
                        </span>
                      </td>
                      <td>{new Date(boleto.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className={`btn btn-sm ${boleto.boleto_html ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => showBoletoHtml(boleto)}
                            title={boleto.boleto_html ? "View boleto HTML" : "No HTML content available"}
                            style={{ marginRight: '5px' }}
                            disabled={!boleto.boleto_html}
                          >
                            View HTML
                          </button>
                          {boleto.pdf_url && (
                            <a 
                              href={boleto.pdf_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-info"
                              style={{ marginRight: '5px' }}
                            >
                              View PDF
                            </a>
                          )}
                          {boleto.barcode && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                navigator.clipboard.writeText(boleto.barcode!);
                                alert('Barcode copied to clipboard!');
                              }}
                              title="Copy barcode"
                              style={{ marginRight: '5px' }}
                            >
                              Copy Barcode
                            </button>
                          )}
                          {boleto.digitable_line && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                navigator.clipboard.writeText(boleto.digitable_line!);
                                alert('Digitable line copied to clipboard!');
                              }}
                              title="Copy digitable line"
                            >
                              Copy Line
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No boletos found for this invoice.</p>
              {invoice.invoice_status !== 'draft' && (
                <p>Click "Create Boleto" to generate a new boleto for this invoice.</p>
              )}
            </div>
          )}

          {boletos.length > 0 && (
            <>
              <div className="boleto-summary" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0 }}>Boleto Summary</h4>
                  <button
                    className={`btn btn-sm ${triggeringStatusUpdate ? 'btn-secondary' : 'btn-info'}`}
                    onClick={triggerPaymentStatusUpdate}
                    disabled={triggeringStatusUpdate}
                    title="Manually check payment status with ePag API and update local records"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px',
                      fontSize: '12px',
                      padding: '6px 12px'
                    }}
                  >
                    {triggeringStatusUpdate ? (
                      <>
                        <div 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            border: '2px solid #fff', 
                            borderTop: '2px solid transparent', 
                            borderRadius: '50%', 
                            animation: 'spin 1s linear infinite' 
                          }} 
                        />
                        Checking...
                      </>
                    ) : (
                      <>
                        🔄 Update Payment Status
                      </>
                    )}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <strong>Total Boletos:</strong> {boletos.length}
                  </div>
                  <div>
                    <strong>With HTML:</strong> {boletos.filter(b => b.boleto_html && b.boleto_html.trim()).length}
                  </div>
                  <div>
                    <strong>Paid:</strong> {boletos.filter(b => ['paid', 'completed'].includes(b.status.toLowerCase())).length}
                  </div>
                  <div>
                    <strong>Pending:</strong> {boletos.filter(b => ['pending', 'processing', 'created'].includes(b.status.toLowerCase())).length}
                  </div>
                  <div>
                    <strong>Expired:</strong> {boletos.filter(b => b.status.toLowerCase() === 'expired').length}
                  </div>
                </div>
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d' }}>
                  <strong>💡 Tip:</strong> Use "Update Payment Status" to manually check if a boleto has been paid and update the invoice status accordingly.
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* HTML Modal */}
      {showHtmlModal && selectedBoletoHtml && (
        <BoletoHtmlModal
          isOpen={showHtmlModal}
          onClose={closeHtmlModal}
          boletoHtml={selectedBoletoHtml}
          boletoNumber={selectedBoletoNumber}
        />
      )}

      <style>{`
        .boletos-tab .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        .boletos-tab .loading {
          text-align: center;
          padding: 40px 20px;
        }

        .boletos-tab .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .boletos-tab .alert {
          padding: 12px 15px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .boletos-tab .alert-danger {
          background-color: #f8d7da;
          border-color: #f5c6cb;
          color: #721c24;
        }

        .boletos-tab .alert-warning {
          background-color: #fff3cd;
          border-color: #ffecb5;
          color: #856404;
        }

        .boletos-tab .alert-info {
          background-color: #d1ecf1;
          border-color: #bee5eb;
          color: #0c5460;
        }

        .boletos-tab .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .boletos-tab .badge-warning {
          background-color: #ffc107;
          color: #212529;
        }

        .boletos-tab .badge-success {
          background-color: #28a745;
          color: white;
        }

        .boletos-tab .badge-danger {
          background-color: #dc3545;
          color: white;
        }

        .boletos-tab .badge-secondary {
          background-color: #6c757d;
          color: white;
        }

        .boletos-tab .badge-info {
          background-color: #17a2b8;
          color: white;
        }

        .boletos-tab .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
        }

        .boletos-tab .text-muted {
          color: #6c757d;
          font-size: 0.9em;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 1200px;
          max-height: 90%;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #333;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #999;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          color: #333;
        }

        .modal-body {
          padding: 20px;
          max-height: calc(90vh - 120px);
          overflow-y: auto;
        }

        .loading-container {
          text-align: center;
          padding: 40px;
        }

        .html-preview {
          width: 100%;
        }

        .html-actions {
          margin-bottom: 15px;
          display: flex;
          gap: 10px;
        }

        .html-actions .btn {
          padding: 8px 16px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f8f9fa;
          cursor: pointer;
          text-decoration: none;
          color: #333;
        }

        .html-actions .btn:hover {
          background: #e9ecef;
        }

        .html-actions .btn-primary {
          background: #007bff;
          color: white;
          border-color: #007bff;
        }

        .html-actions .btn-primary:hover {
          background: #0056b3;
        }

        .html-actions .btn-info {
          background: #17a2b8;
          color: white;
          border-color: #17a2b8;
        }

        .html-actions .btn-info:hover {
          background: #138496;
        }

        /* Debug Section Styles */
        .boletos-tab .debug-section {
          transition: all 0.3s ease;
        }

        .boletos-tab .debug-section .btn-outline-secondary {
          border: 1px solid #6c757d;
          color: #6c757d;
          background: transparent;
          transition: all 0.2s ease;
        }

        .boletos-tab .debug-section .btn-outline-secondary:hover {
          background: #6c757d;
          color: white;
        }

        .boletos-tab .debug-components {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceDetailsBoletosTab;